/**
 * Phase 6c PR 6 — `CoachMessage` data model + selection rule.
 *
 * The Phase 6c CoachCard surface stops being author-written JSX content
 * and becomes a presentational shell driven by a `CoachMessage` record.
 * In production the record comes from a server-side rules engine that
 * watches activity (sleep, sessions, plans, social signals) and decides
 * which of the 5 coaches speaks today.
 *
 * For now (Phase 6c — no rules engine yet) the message is sourced
 * locally from `mockCoachMessages` factories below. PR 6's job is the
 * data shape + selection rule + presentational refactor; the rules
 * engine is a Phase 6b/7 follow-up.
 *
 * Per direction.md §3.5 + HANDOFF.md §"PR 6":
 *   - 5 trigger kinds: active / pattern / hello / mention / plan-shipped
 *   - Priority on ties: plan-shipped > mention > hello > pattern > active
 *   - At most ONE message rendered per /v2/today render (the lead
 *     CoachCard slot). Future tiers may show secondary cards.
 */

export type CoachId = 'training' | 'gear' | 'race' | 'recovery' | 'mental';

/**
 * Display labels for the 5 coach voices. Matches the existing fixture
 * `--c-{id}` token mapping in tokens.css and the coaches.ts identity
 * map. Re-stated here to keep this module pure (no fixture import).
 */
export const COACH_DISPLAY: Record<CoachId, { name: string; label: string }> = {
  training: { name: 'Airo', label: 'Training' },
  gear: { name: 'Gair', label: 'Gear' },
  race: { name: 'Vaira', label: 'Race' },
  recovery: { name: 'Onai', label: 'Recovery' },
  mental: { name: 'Kairu', label: 'Mental' },
};

export type ISODate = string;

export type PatternSignal =
  | 'sleep-debt'
  | 'pace-drift'
  | 'streak-break-risk'
  | 'mileage-spike'
  | 'low-motivation';

export type CoachMessageTrigger =
  | { kind: 'active'; ruleId: string }
  | { kind: 'pattern'; windowDays: number; signal: PatternSignal }
  | { kind: 'hello'; firstSeenAt: ISODate }
  | { kind: 'mention'; sourceCoach: CoachId; sourceMessageId: string }
  | { kind: 'plan-shipped'; planId: string };

export type CoachMessageTriggerKind = CoachMessageTrigger['kind'];

export interface CoachMessage {
  /** Stable id; used for dedupe + telemetry. */
  id: string;
  /** Drives `--c-{coach}` colour. */
  coach: CoachId;
  /** The WHY — drives `?coach=` URL param + telemetry tag. */
  trigger: CoachMessageTrigger;
  /** "Active · Airo" / "Pattern · Onai" / "Hello · Kairu" / "Mention · Vaira" / "Plan-shipped · Airo" */
  kicker: string;
  /** "just now" / "3 sessions" / "first visit" / "linked from Airo" / "2 min ago" */
  rightMeta?: string;
  /** h3 — Instrument Serif, ≤ 60 chars. */
  title: string;
  /** 1–2 sentences plain. May contain a single `<em>` for emphasis. */
  body: string;
  /** "Ask Airo ›" / "Meet Kairu ›" / "Open preview ›" / "Review plan ›". */
  cta: { label: string; href: string };
  /**
   * Hide after. Default windows:
   *   active: 24h
   *   pattern: 7d
   *   hello / plan-shipped: never (treated as omnipresent until acted on)
   *   mention: 24h
   * Caller is responsible for filtering by `expiresAt` before the
   * selection rule fires.
   */
  expiresAt: ISODate;
}

/**
 * Selection priority. Higher number wins.
 *
 *   plan-shipped (5) — always wins; it's a hard moment
 *   mention (4)      — a coach explicitly handed off
 *   hello (3)        — first contact with a new coach
 *   pattern (2)      — rules engine fired
 *   active (1)       — default fallback (phase-aligned coach)
 */
const TRIGGER_PRIORITY: Record<CoachMessageTriggerKind, number> = {
  'plan-shipped': 5,
  mention: 4,
  hello: 3,
  pattern: 2,
  active: 1,
};

/**
 * Pick the highest-priority CoachMessage from the candidate list. Ties
 * broken by `expiresAt` (earlier wins — about-to-expire messages are
 * more urgent). Returns null when the list is empty or every message
 * has already expired.
 *
 * Pure function — caller is responsible for the `now` clock so this
 * stays unit-testable. Production callers pass `new Date()`.
 */
export function selectCoachMessage(
  candidates: CoachMessage[],
  now: Date = new Date(),
): CoachMessage | null {
  if (candidates.length === 0) return null;
  const live = candidates.filter((m) => {
    const exp = Date.parse(m.expiresAt);
    if (Number.isNaN(exp)) return true; // malformed → keep, don't drop silently
    return exp > now.getTime();
  });
  if (live.length === 0) return null;
  let best = live[0]!;
  let bestPriority = TRIGGER_PRIORITY[best.trigger.kind];
  let bestExp = Date.parse(best.expiresAt);
  for (const m of live.slice(1)) {
    const p = TRIGGER_PRIORITY[m.trigger.kind];
    const exp = Date.parse(m.expiresAt);
    if (p > bestPriority) {
      best = m;
      bestPriority = p;
      bestExp = exp;
    } else if (p === bestPriority && exp < bestExp) {
      // Same priority, earlier expiration → more urgent
      best = m;
      bestExp = exp;
    }
  }
  return best;
}

// ─── Mock factories — used by /v2/today demo path + tests ───────────

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_MS = 7 * ONE_DAY_MS;

function isoOffset(now: Date, ms: number): ISODate {
  return new Date(now.getTime() + ms).toISOString();
}

/**
 * 5 ready-to-render mock CoachMessage records — one per trigger kind.
 * Used by the /v2/today demo path (`?user=ramiro&coach=hello` etc.) and
 * as test seeds. The real production source is the rules engine; these
 * are stable copy for the design preview.
 *
 * `now` is pinned at call time so `expiresAt` rolls forward with the
 * caller's clock — important for the test clock and for keeping the
 * messages "live" across demo sessions.
 */
export function mockCoachMessages(now: Date = new Date()): Record<
  CoachMessageTriggerKind,
  CoachMessage
> {
  return {
    active: {
      id: 'mock-active-1',
      coach: 'training',
      trigger: { kind: 'active', ruleId: 'taper-week-anchor' },
      kicker: 'Active · Airo',
      rightMeta: 'just now',
      title: 'Dial back to 80%: race week earns its keep in the warm-up.',
      body: "You've put in the work. <em>Now</em> the rule is: easy is easy. If your watch beeps under 9:00, you're going too hard. Two strides on Saturday, then we line up.",
      cta: { label: 'Ask Airo', href: '/v2/chat?coach=training' },
      expiresAt: isoOffset(now, ONE_DAY_MS),
    },
    pattern: {
      id: 'mock-pattern-1',
      coach: 'recovery',
      trigger: { kind: 'pattern', windowDays: 3, signal: 'sleep-debt' },
      kicker: 'Pattern · Onai',
      rightMeta: '3 sessions',
      title: 'Sleep debt is trending: protect 8 hours tonight.',
      body: 'Your last three sessions came after <em>under-7-hour nights</em>. Pace drift on the 1600s tracks with it. Sleep, not effort, is the move tonight.',
      cta: { label: 'Ask Onai', href: '/v2/chat?coach=recovery' },
      expiresAt: isoOffset(now, SEVEN_DAYS_MS),
    },
    hello: {
      id: 'mock-hello-1',
      coach: 'mental',
      trigger: { kind: 'hello', firstSeenAt: now.toISOString() },
      kicker: 'Hello · Kairu',
      rightMeta: 'first visit',
      title: "Hey, I'm Kairu. I work the head game.",
      body: 'Pre-race nerves, mid-session doubts, race-day breathing: that\'s my lane. Two minutes is plenty. <em>Try me</em> Saturday morning before the opener.',
      cta: { label: 'Meet Kairu', href: '/v2/chat?coach=mental' },
      expiresAt: isoOffset(now, 30 * ONE_DAY_MS), // hello messages stick around
    },
    mention: {
      id: 'mock-mention-1',
      coach: 'race',
      trigger: { kind: 'mention', sourceCoach: 'training', sourceMessageId: 'src-1' },
      kicker: 'Mention · Vaira',
      rightMeta: 'linked from Airo',
      title: 'Course is hillier than last year: pacing matters more than fitness.',
      body: "Airo flagged you for me. The State Park 4 course gained <em>140 ft</em> at the south turn since last spring. Don't lead the first mile.",
      cta: { label: 'Open preview', href: '/v2/chat?coach=race' },
      expiresAt: isoOffset(now, ONE_DAY_MS),
    },
    'plan-shipped': {
      id: 'mock-plan-shipped-1',
      coach: 'training',
      trigger: { kind: 'plan-shipped', planId: 'plan-spring-2026' },
      kicker: 'Plan-shipped · Airo',
      rightMeta: '2 min ago',
      title: 'Your Spring 2026 plan is live.',
      body: '7 weeks, 4 meets, taper and recovery built in. Open the week-by-week to see <em>where the work lands</em>. Edit anything before Monday.',
      cta: { label: 'Review plan', href: '/v2/plan' },
      expiresAt: isoOffset(now, 30 * ONE_DAY_MS),
    },
  };
}
